import React from 'react'
import {
    DocumentCard,
    DocumentCardDetails,
    DocumentCardPreview,
    DocumentCardStatus,
    DocumentCardTitle,
    DocumentCardType,
    getTheme,
    mergeStyleSets,
    Stack
} from "@fluentui/react";
import {useHistory} from "react-router-dom";
import {
    useGetApiKeysQuery,
    useGetCatalogsQuery,
    useGetInstalledExtensionsQuery,
    useGetSubOrganizationsQuery, useGetTaxCodesQuery
} from "../../store/Api";
import {RootState} from "../../store/rootReducer";
import {useSelector} from "react-redux";
import useCan, {
    CATALOG_MODIFY,
    FOLIO_AUTHOR,
    ORG_EXTENSION_MODIFY,
    ORG_MODIFY, PROCUREMENT,
    STOREFRONT_ADMIN,
    TAX_CODE_MODIFY, USER_EXTENSION
} from "../../permissions/ability";
import Footer from "../../components/Layout/Footer";
import {useGetOrganizationEntitlementsQuery} from "../../store/endpoints/OrganizationsEndpoints";

const classes = mergeStyleSets({
    cell: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: '1em',
        float: 'left',
        height: '10em',
        width: '10em',
        padding: '2em',
        textAlign: 'center',
        border: '1px solid #ccc',
        borderRadius: '4px',
        cursor: 'pointer',
    },
    icon: {
        fontSize: '50px',
        marginTop: '0.5em',
    },
    header: {
        marginTop: '1.5em',
        marginBottom: '0.5em',
        fontSize: '1.4em',
    },
    secondaryHeader: {
        '@media(max-width: 600px)': {
            display: 'none',
        }
    }
});

const SettingsHomePage: React.FC = () => {
    const organization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const getOrgEntitlements = useGetOrganizationEntitlementsQuery(organization ?? '', {skip: !organization || organization === ''});
    const catalogs = useGetCatalogsQuery();
    const subOrganizations = useGetSubOrganizationsQuery();
    const extensions = useGetInstalledExtensionsQuery();
    const apiKeys = useGetApiKeysQuery();
    const taxCodes = useGetTaxCodesQuery();
    const canOrgModify = useCan(ORG_MODIFY);
    const canCatalogModify = useCan(CATALOG_MODIFY);
    const canTaxCodeModify = useCan(TAX_CODE_MODIFY);
    const canOrgExtensionModify = useCan(ORG_EXTENSION_MODIFY);
    // Entitlement-gated server side, so the claim's absence is the whole check here.
    const canAuthorFolios = useCan(FOLIO_AUTHOR);
    const canUserExtension = useCan(USER_EXTENSION);
    const canStorefrontAdmin = useCan(STOREFRONT_ADMIN);
    const canProcurement = useCan(PROCUREMENT);
    
    return (
        <div style={{padding: '1em'}}>
            <h1>Settings</h1>
            <h2>Organization Settings</h2>
            <Stack tokens={{childrenGap: '1em'}} horizontal wrap>
                {canOrgModify && <SettingsNavDoc
                    icon='CityNext2'
                    header='Organization'
                    route={'/settings/organization'}
                    secondaryHeader='Update organization level settings'
                    compact
                />}
                <SettingsNavDoc
                    icon='Search'
                    header='Search Settings'
                    route={'/settings/search'}
                    secondaryHeader='Update user & global search settings'
                    compact
                />
                {canOrgModify && <SettingsNavDoc
                    icon='DeliveryTruck'
                    header='Vendor Management'
                    route={'/settings/vendors'}
                    secondaryHeader='Manage the display of vendors'
                    compact
                />}
                {canCatalogModify && <SettingsNavDoc
                    icon='ProductCatalog'
                    header='Catalog Management'
                    route={'/settings/catalogs'}
                    secondaryHeader='Create and manage product catalogs'
                    status={catalogs.isLoading ? 'Loading...' : `${catalogs.data?.length} of ${getOrgEntitlements.data?.catalogLimit ?? 1} catalogs`}
                    compact
                />}
                 <SettingsNavDoc
                    icon='TripleColumn'
                    header='Product Category Management'
                    route={'/settings/categories'}
                    secondaryHeader='Modify product categories'
                    compact
                />
                <SettingsNavDoc
                    icon='AccountManagement'
                    header='User Management'
                    route={'/settings/users'}
                    secondaryHeader='Create and manage users'
                    status={getOrgEntitlements.data ? `${getOrgEntitlements.data.licensedUserCount ?? 1} of ${getOrgEntitlements.data.userLimit} users` : undefined}
                    compact
                />
                <SettingsNavDoc
                    icon='ContactInfo'
                    header='My User'
                    route={'/settings/myUser'}
                    secondaryHeader='Change your user settings'
                    compact
                />
                {canTaxCodeModify && <SettingsNavDoc
                    icon='Money'
                    header='Tax Code Management'
                    route={'/settings/taxCodes'}
                    secondaryHeader='Create and manage internal & imported tax codes'
                    status={taxCodes.isLoading ? 'Loading...' : `${taxCodes.data?.length} tax codes`}
                    compact
                />}
                {getOrgEntitlements.data?.subOrganizationsEnabled && <SettingsNavDoc
                    icon='Org'
                    header='Sub-Org Management'
                    route={'/settings/subOrganizations'}
                    secondaryHeader='Create and manage sub-organizations'
                    status={subOrganizations.isLoading ? 'Loading...' : `${subOrganizations.data?.length} sub-organizations`}
                    compact
                />}
                {canProcurement && <>
                    <SettingsNavDoc
                        icon='DeliveryTruck'
                        header='Procurement'
                        route='/settings/procurement'
                        secondaryHeader='Manage procurement settings'
                        compact
                    />
                </>}
                <SettingsNavDoc
                    icon='Contact'
                    header='Contact Management'
                    route='/settings/contacts'
                    secondaryHeader='Manage contacts'
                    compact
                />
            </Stack>
            <h2>Extensions</h2>
            <Stack tokens={{childrenGap: '1em'}} horizontal wrap>
                {(canOrgExtensionModify || canUserExtension) && <SettingsNavDoc
                    icon='Link'
                    header='Extension Management'
                    route={'/settings/extensions'}
                    secondaryHeader='Manage installed extensions'
                    status={extensions.isLoading ? 'Loading...' : `${extensions.data?.length} installed extensions`}
                    compact
                />}
                {canAuthorFolios && <SettingsNavDoc
                    icon='Trackers'
                    header='Extension Folios'
                    route={'/settings/folios'}
                    secondaryHeader='Author connectors that bundle capabilities behind one set of credentials'
                    compact
                />}
                <SettingsNavDoc
                    icon='Lock'
                    header='API Keys'
                    route={'/settings/apikeys'}
                    secondaryHeader='Manage API used for 3rd party inbound integrations'
                    status={apiKeys.isLoading ? 'Loading...' : `${apiKeys.data?.length} API keys`}
                    compact
                />
                <SettingsNavDoc
                    icon='Flow'
                    header='Webhooks'
                    route={'/settings/webhooks'}
                    secondaryHeader='Manage outbound webhooks'
                    compact
                />
            </Stack>
            {canStorefrontAdmin && <>
                <h2>Storefront</h2>
                <Stack tokens={{childrenGap: '1em'}} horizontal wrap>
                    <SettingsNavDoc
                        icon='ReportDocument'
                        header='Storefront Dashboard'
                        route='/settings/storefront/dashboard'
                        secondaryHeader='View storefront performance'
                        compact
                    />
                    <SettingsNavDoc
                        icon='ShoppingCart'
                        header='Storefront Ordering & Carts Settings'
                        route='/settings/storefront/ordering'
                        secondaryHeader='Manage global ordering and cart settings'
                        compact
                    />
                    <SettingsNavDoc
                        icon='Product'
                        header='Storefront Product Settings'
                        route='/settings/storefront/products'
                        secondaryHeader='Manage global product display settings'
                        compact
                    />
                    <SettingsNavDoc
                        icon='Shop'
                        header='Storefronts'
                        route='/settings/storefront/storefronts'
                        secondaryHeader='View and manage storefronts'
                        compact
                    />
                </Stack>
            </>}
            {getOrgEntitlements.data?.apiUsageBillingEnabled && 
            <React.Fragment>
                <h2>Reporting</h2>
                <SettingsNavDoc
                    icon='ReportDocument'
                    header='API Usage'
                    route={'/settings/reporting/api'}
                    secondaryHeader='View your API Usage'
                    compact
                />
            </React.Fragment>}
            <Footer className={'powered-by-footer-absolute'}/>
        </div>
    )
}

interface ISettingsNavSquareProps {
    icon: string;
    header: string;
    route: string;
    secondaryHeader?: string;
    status?: string;
    compact?: boolean;
}

const SettingsNavDoc: React.FC<ISettingsNavSquareProps> = (props) => {
    const history = useHistory();
    const theme = getTheme();
    const { palette, fonts } = theme;
    const nav = () => {
        history.push(props.route);
    }
    return (
        <DocumentCard
            onClick={nav}
            type={props.compact ? DocumentCardType.compact : DocumentCardType.normal}
            style={{
                width: props.compact ? '100%' : '300px',
                maxWidth: props.compact ? '500px' : undefined,
            }}
        >
            <DocumentCardPreview
                previewImages={[{
                    previewIconProps: {
                        iconName: props.icon,
                        styles: {
                            root: {
                                fontSize: fonts.superLarge.fontSize,
                                color: '#0078d7',
                                backgroundColor: palette.neutralLighterAlt,
                            },
                        },
                    },
                    width: props.compact ? 75 : undefined,
                    height: props.compact ? undefined : 200,
                }]}
            />
            <DocumentCardDetails>
                <DocumentCardTitle title={props.header} styles={{root: {fontWeight: 'bold'}}}/>
                {props.secondaryHeader && <DocumentCardTitle title={props.secondaryHeader} showAsSecondaryTitle className={classes.secondaryHeader} /> }
                {props.status && <DocumentCardStatus status={props.status} />}
            </DocumentCardDetails>
        </DocumentCard>
    )
}

export default SettingsHomePage;